import React, { useEffect, useState } from 'react';
import { MessageSquareIcon, UserIcon, SendIcon, EyeIcon, EyeOffIcon, UsersIcon, GlobeIcon, PlusIcon, FilterIcon, ChevronDownIcon, SearchIcon, CheckIcon, XIcon, ArrowLeftIcon, PaperclipIcon, DownloadIcon, ClockIcon, ExternalLinkIcon, FileTextIcon, MinusSquareIcon } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyFormatter';
import { NoteVisibility } from '../../settings/notes/NotesManagement';
// Visibility types for communications
export type MessageVisibility = 'Public' | 'Internal' | 'Group' | 'Private';
// Communication message type
export type Message = {
  id: number;
  sender: string;
  senderRole: string;
  timestamp: string;
  content: string;
  attachments?: {
    id: string;
    name: string;
    size: string;
    type: string;
    url: string;
  }[];
  read: boolean;
  visibility: MessageVisibility;
  visibilityGroups?: string[];
  sourceType?: 'Communication' | 'Invoice' | 'CreditNote';
  sourceId?: string;
  sourceReference?: string;
};
// Thread type
export type Thread = {
  id: number;
  title: string;
  entityName: string;
  entityId: string;
  module: 'PO' | 'Invoice' | 'Requisition' | 'Vendor' | 'Contract' | 'Payment' | 'CreditNote';
  moduleId: string;
  status: 'Active' | 'Resolved' | 'Pending' | 'Closed';
  priority: 'High' | 'Medium' | 'Low';
  lastActivity: string;
  participants: {
    id: string;
    name: string;
    role: string;
    isVendor: boolean;
  }[];
  messages: Message[];
  tags?: string[];
};
// Interface for converting between notes and communications
interface NoteSource {
  id: string;
  moduleType: string;
  moduleId: string;
  moduleName: string;
  noteTypeId: string;
  noteTypeName: string;
  visibility: NoteVisibility;
  visibilityDetails?: string;
  body: string;
  authorId: string;
  authorName: string;
  createdAt: string;
}
export function CommunicationsView({
  locale = 'en-US'
}) {
  const [activeView, setActiveView] = useState<'threads' | 'thread-detail' | 'notes'>('threads');
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [messageVisibility, setMessageVisibility] = useState<MessageVisibility>('Internal'); // Default to internal visibility
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [showVisibilitySelector, setShowVisibilitySelector] = useState(false);
  const [filter, setFilter] = useState({
    status: 'all',
    module: 'all',
    priority: 'all',
    search: '',
    source: 'all'
  });
  const [currentUserRole, setCurrentUserRole] = useState('Procurement Officer');
  const [isCurrentUserVendor, setIsCurrentUserVendor] = useState(false);
  const [showNoteImport, setShowNoteImport] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState<string[]>([]);
  // Sample user groups data
  const userGroups = [{
    id: 'group-1',
    name: 'Finance Team'
  }, {
    id: 'group-2',
    name: 'Procurement Team'
  }, {
    id: 'group-3',
    name: 'Executive Team'
  }, {
    id: 'group-4',
    name: 'IT Department'
  }, {
    id: 'group-5',
    name: 'Vendor Management'
  }];
  // Sample notes from invoices and credit notes
  const [relatedNotes, setRelatedNotes] = useState<NoteSource[]>([{
    id: 'note-inv-001',
    moduleType: 'Invoice',
    moduleId: 'INV-2023-042',
    moduleName: 'Marketing Materials',
    noteTypeId: 'notetype-001',
    noteTypeName: 'General Comment',
    visibility: 'Internal',
    body: 'Vendor has confirmed this invoice is related to the Q4 marketing campaign. Please allocate to the correct cost center.',
    authorId: 'user-003',
    authorName: 'Emily Chen',
    createdAt: '2023-10-20T13:15:00Z'
  }, {
    id: 'note-inv-002',
    moduleType: 'Invoice',
    moduleId: 'INV-2023-045',
    moduleName: 'IT Equipment',
    noteTypeId: 'notetype-002',
    noteTypeName: 'Approval Note',
    visibility: 'Role-based',
    visibilityDetails: 'Finance Team, Procurement Team',
    body: 'This invoice exceeds the PO amount by 5%. Approved as per delegation authority, but please check with vendor about price increases for future orders.',
    authorId: 'user-001',
    authorName: 'John Smith',
    createdAt: '2023-10-22T09:45:00Z'
  }, {
    id: 'note-cn-001',
    moduleType: 'CreditNote',
    moduleId: 'CN-2023-012',
    moduleName: 'Office Supplies Return',
    noteTypeId: 'notetype-001',
    noteTypeName: 'General Comment',
    visibility: 'Public',
    body: "Credit note received for returned items from last month's order. Quality issues were reported to the vendor.",
    authorId: 'user-002',
    authorName: 'Sarah Williams',
    createdAt: '2023-10-19T16:20:00Z'
  }, {
    id: 'note-cn-002',
    moduleType: 'CreditNote',
    moduleId: 'CN-2023-015',
    moduleName: 'Service Credit',
    noteTypeId: 'notetype-003',
    noteTypeName: 'Audit Comment',
    visibility: 'Role-based',
    visibilityDetails: 'Finance Manager, Auditor',
    body: 'This credit note relates to service level agreement failures in September. Documentation has been verified and attached to the record.',
    authorId: 'user-005',
    authorName: 'Robert Brown',
    createdAt: '2023-10-24T11:30:00Z'
  }]);
  // Sample threads data
  const [threads, setThreads] = useState<Thread[]>([{
    id: 1,
    title: 'Server Hardware Purchase Order Clarification',
    entityName: 'Global Technologies Inc.',
    entityId: 'ent-001',
    module: 'PO',
    moduleId: 'PO-2023-001',
    status: 'Active',
    priority: 'High',
    lastActivity: '2023-10-25T14:30:00Z',
    participants: [{
      id: 'user-001',
      name: 'John Smith',
      role: 'Procurement Officer',
      isVendor: false
    }, {
      id: 'user-002',
      name: 'Sarah Williams',
      role: 'Finance Manager',
      isVendor: false
    }, {
      id: 'vendor-001',
      name: 'Michael Johnson',
      role: 'Account Manager',
      isVendor: true
    }],
    messages: [{
      id: 1,
      sender: 'John Smith',
      senderRole: 'Procurement Officer',
      timestamp: '2023-10-22T10:15:00Z',
      content: 'We need to clarify the delivery timeline for the server hardware. Can you confirm if the December 15th date is still achievable?',
      read: true,
      visibility: 'Public',
      sourceType: 'Communication'
    }, {
      id: 2,
      sender: 'Robert Martinez',
      senderRole: 'Procurement Officer',
      timestamp: '2023-10-23T16:45:00Z',
      content: "We acknowledge receipt of your request. Our team will review the cost analysis and schedule a meeting to discuss this further. We'll aim to provide a response by the end of this week.",
      read: false,
      visibility: 'Public',
      sourceType: 'Communication'
    }, {
      id: 3,
      sender: 'Sarah Williams',
      senderRole: 'Finance Manager',
      timestamp: '2023-10-24T09:20:00Z',
      content: "I've reviewed the budget allocation for this purchase. We need to discuss some potential adjustments to stay within Q4 limits.",
      attachments: [{
        id: 'att-001',
        name: 'Q4_Budget_Analysis.xlsx',
        size: '1.2 MB',
        type: 'spreadsheet',
        url: '#'
      }],
      read: true,
      visibility: 'Internal',
      sourceType: 'Communication'
    }, {
      id: 4,
      sender: 'Michael Johnson',
      senderRole: 'Account Manager',
      timestamp: '2023-10-25T14:30:00Z',
      content: 'Yes, we can still meet the December 15th delivery date. However, there might be additional shipping costs due to the holiday season rush. Would you like us to provide a revised quote?',
      read: false,
      visibility: 'Public',
      sourceType: 'Communication'
    }],
    tags: ['Urgent', 'Budget Review', 'Q4 Delivery']
  }, {
    id: 2,
    title: 'Marketing Materials Invoice Discrepancy',
    entityName: 'Global Technologies Inc.',
    entityId: 'ent-001',
    module: 'Invoice',
    moduleId: 'INV-2023-042',
    status: 'Pending',
    priority: 'Medium',
    lastActivity: '2023-10-21T11:05:00Z',
    participants: [{
      id: 'user-001',
      name: 'John Smith',
      role: 'Procurement Officer',
      isVendor: false
    }, {
      id: 'user-003',
      name: 'Emily Chen',
      role: 'AP Specialist',
      isVendor: false
    }, {
      id: 'vendor-002',
      name: 'Lisa Wong',
      role: 'Billing Coordinator',
      isVendor: true
    }],
    messages: [{
      id: 1,
      sender: 'Emily Chen',
      senderRole: 'AP Specialist',
      timestamp: '2023-10-20T15:30:00Z',
      content: 'We\'ve identified a discrepancy in invoice #INV-2023-042. The quantity for line item "Brochures" shows 500 units, but our PO specified 450 units. Please clarify.',
      attachments: [{
        id: 'att-002',
        name: 'Invoice_Discrepancy_Details.pdf',
        size: '845 KB',
        type: 'document',
        url: '#'
      }],
      read: true,
      visibility: 'Public',
      sourceType: 'Communication'
    }, {
      id: 2,
      sender: 'Lisa Wong',
      senderRole: 'Billing Coordinator',
      timestamp: '2023-10-21T11:05:00Z',
      content: "Thank you for bringing this to our attention. You're correct about the discrepancy. We'll issue a corrected invoice today. The additional 50 units were from a verbal request, but we understand this needs to be properly documented.",
      read: false,
      visibility: 'Public',
      sourceType: 'Communication'
    }, {
      id: 3,
      sender: 'Emily Chen',
      senderRole: 'AP Specialist',
      timestamp: '2023-10-20T13:15:00Z',
      content: 'Vendor has confirmed this invoice is related to the Q4 marketing campaign. Please allocate to the correct cost center.',
      read: true,
      visibility: 'Internal',
      sourceType: 'Invoice',
      sourceId: 'note-inv-001',
      sourceReference: 'INV-2023-042'
    }]
  }, {
    id: 3,
    title: 'Office Furniture Contract Terms',
    entityName: 'European Operations Ltd',
    entityId: 'ent-002',
    module: 'Contract',
    moduleId: 'CON-2023-015',
    status: 'Active',
    priority: 'Low',
    lastActivity: '2023-10-19T10:45:00Z',
    participants: [{
      id: 'user-004',
      name: 'David Wilson',
      role: 'Contract Manager',
      isVendor: false
    }, {
      id: 'user-005',
      name: 'Anna Schmidt',
      role: 'Legal Counsel',
      isVendor: false
    }, {
      id: 'vendor-003',
      name: 'Thomas Brown',
      role: 'Sales Director',
      isVendor: true
    }],
    messages: [{
      id: 1,
      sender: 'David Wilson',
      senderRole: 'Contract Manager',
      timestamp: '2023-10-18T14:20:00Z',
      content: "We're reviewing the maintenance terms in section 5.3 of the contract. Can we extend the response time for urgent issues from 8 hours to 4 hours?",
      read: true,
      visibility: 'Public',
      sourceType: 'Communication'
    }, {
      id: 2,
      sender: 'Anna Schmidt',
      senderRole: 'Legal Counsel',
      timestamp: '2023-10-18T16:40:00Z',
      content: "I've reviewed the liability clauses and have some concerns about the limitations. Let's discuss internally before our next vendor call.",
      read: true,
      visibility: 'Internal',
      visibilityGroups: ['Executive Team', 'Legal Team'],
      sourceType: 'Communication'
    }, {
      id: 3,
      sender: 'Thomas Brown',
      senderRole: 'Sales Director',
      timestamp: '2023-10-19T10:45:00Z',
      content: "We can accommodate the 4-hour response time for urgent issues. I'll have our team update the contract draft and send it over for your review.",
      read: false,
      visibility: 'Public',
      sourceType: 'Communication'
    }]
  }, {
    id: 4,
    title: 'Office Supplies Return - Credit Note Clarification',
    entityName: 'Global Technologies Inc.',
    entityId: 'ent-001',
    module: 'CreditNote',
    moduleId: 'CN-2023-012',
    status: 'Active',
    priority: 'Medium',
    lastActivity: '2023-10-19T16:20:00Z',
    participants: [{
      id: 'user-002',
      name: 'Sarah Williams',
      role: 'Finance Manager',
      isVendor: false
    }, {
      id: 'vendor-004',
      name: 'James Wilson',
      role: 'Customer Service Rep',
      isVendor: true
    }],
    messages: [{
      id: 1,
      sender: 'Sarah Williams',
      senderRole: 'Finance Manager',
      timestamp: '2023-10-19T16:20:00Z',
      content: "Credit note received for returned items from last month's order. Quality issues were reported to the vendor.",
      read: true,
      visibility: 'Public',
      sourceType: 'CreditNote',
      sourceId: 'note-cn-001',
      sourceReference: 'CN-2023-012'
    }]
  }]);
  // Handle thread selection
  const handleSelectThread = (thread: Thread) => {
    setSelectedThread(thread);
    setActiveView('thread-detail');
  };
  // Handle sending a new message
  const handleSendMessage = () => {
    if (!newMessage.trim() || !selectedThread) return;
    const updatedThreads = threads.map(thread => {
      if (thread.id === selectedThread.id) {
        const newMessageObj: Message = {
          id: thread.messages.length + 1,
          sender: 'John Smith',
          senderRole: 'Procurement Officer',
          timestamp: new Date().toISOString(),
          content: newMessage,
          read: true,
          visibility: messageVisibility,
          sourceType: 'Communication',
          ...(messageVisibility === 'Group' && {
            visibilityGroups: selectedGroups
          })
        };
        return {
          ...thread,
          messages: [...thread.messages, newMessageObj],
          lastActivity: new Date().toISOString()
        };
      }
      return thread;
    });
    setThreads(updatedThreads);
    setSelectedThread(updatedThreads.find(t => t.id === selectedThread.id) || null);
    setNewMessage('');
    setMessageVisibility('Internal'); // Reset to default
    setSelectedGroups([]);
  };
  // Convert note to message and add to thread
  const convertNoteToMessage = (note: NoteSource) => {
    // Check if there's already a thread for this module/id
    let targetThread = threads.find(t => t.moduleType === note.moduleType && t.moduleId === note.moduleId);
    // Convert note visibility to message visibility
    const convertVisibility = (noteVis: NoteVisibility): MessageVisibility => {
      switch (noteVis) {
        case 'Public':
          return 'Public';
        case 'Internal':
          return 'Internal';
        case 'Role-based':
          return 'Group';
        case 'User-specific':
          return 'Group';
        case 'Creator-only':
          return 'Private';
        default:
          return 'Internal';
      }
    };
    const newMessage: Message = {
      id: Date.now(),
      sender: note.authorName,
      senderRole: 'System',
      timestamp: note.createdAt,
      content: note.body,
      read: true,
      visibility: convertVisibility(note.visibility),
      visibilityGroups: note.visibilityDetails ? [note.visibilityDetails] : undefined,
      sourceType: note.moduleType as 'Invoice' | 'CreditNote',
      sourceId: note.id,
      sourceReference: note.moduleId
    };
    if (targetThread) {
      // Add message to existing thread
      const updatedThreads = threads.map(thread => {
        if (thread.id === targetThread!.id) {
          return {
            ...thread,
            messages: [...thread.messages, newMessage],
            lastActivity: note.createdAt
          };
        }
        return thread;
      });
      setThreads(updatedThreads);
    } else {
      // Create new thread
      const newThread: Thread = {
        id: threads.length + 1,
        title: `${note.moduleType} ${note.moduleId} - ${note.moduleName}`,
        entityName: 'Global Technologies Inc.',
        entityId: 'ent-001',
        module: note.moduleType as any,
        moduleId: note.moduleId,
        status: 'Active',
        priority: 'Medium',
        lastActivity: note.createdAt,
        participants: [{
          id: note.authorId,
          name: note.authorName,
          role: 'System',
          isVendor: false
        }],
        messages: [newMessage],
        tags: [note.noteTypeName]
      };
      setThreads([...threads, newThread]);
    }
  };
  // Handle back to threads list
  const handleBackToThreads = () => {
    setSelectedThread(null);
    setActiveView('threads');
  };
  // Toggle group selection
  const handleToggleGroup = (groupName: string) => {
    if (selectedGroups.includes(groupName)) {
      setSelectedGroups(selectedGroups.filter(g => g !== groupName));
    } else {
      setSelectedGroups([...selectedGroups, groupName]);
    }
  };
  // Toggle note selection for import
  const handleToggleNoteSelection = (noteId: string) => {
    if (selectedNotes.includes(noteId)) {
      setSelectedNotes(selectedNotes.filter(id => id !== noteId));
    } else {
      setSelectedNotes([...selectedNotes, noteId]);
    }
  };
  // Import selected notes to current thread
  const handleImportNotes = () => {
    if (!selectedThread || selectedNotes.length === 0) return;
    const notesToImport = relatedNotes.filter(note => selectedNotes.includes(note.id));
    const newMessages = notesToImport.map(note => {
      const convertVisibility = (noteVis: NoteVisibility): MessageVisibility => {
        switch (noteVis) {
          case 'Public':
            return 'Public';
          case 'Internal':
            return 'Internal';
          case 'Role-based':
            return 'Group';
          case 'User-specific':
            return 'Group';
          case 'Creator-only':
            return 'Private';
          default:
            return 'Internal';
        }
      };
      return {
        id: Date.now() + Math.floor(Math.random() * 1000),
        sender: note.authorName,
        senderRole: 'System',
        timestamp: note.createdAt,
        content: note.body,
        read: true,
        visibility: convertVisibility(note.visibility),
        visibilityGroups: note.visibilityDetails ? [note.visibilityDetails] : undefined,
        sourceType: note.moduleType as 'Invoice' | 'CreditNote',
        sourceId: note.id,
        sourceReference: note.moduleId
      };
    });
    const updatedThreads = threads.map(thread => {
      if (thread.id === selectedThread.id) {
        return {
          ...thread,
          messages: [...thread.messages, ...newMessages],
          lastActivity: new Date().toISOString()
        };
      }
      return thread;
    });
    setThreads(updatedThreads);
    setSelectedThread(updatedThreads.find(t => t.id === selectedThread.id) || null);
    setSelectedNotes([]);
    setShowNoteImport(false);
  };
  // Filter threads based on current filter settings
  const filteredThreads = threads.filter(thread => {
    // Filter by status
    if (filter.status !== 'all' && thread.status.toLowerCase() !== filter.status) {
      return false;
    }
    // Filter by module
    if (filter.module !== 'all' && thread.module.toLowerCase() !== filter.module.toLowerCase()) {
      return false;
    }
    // Filter by priority
    if (filter.priority !== 'all' && thread.priority.toLowerCase() !== filter.priority) {
      return false;
    }
    // Filter by source
    if (filter.source !== 'all') {
      // Check if any message has the matching source type
      if (filter.source === 'notes') {
        return thread.messages.some(m => m.sourceType === 'Invoice' || m.sourceType === 'CreditNote');
      } else if (filter.source === 'communications') {
        return thread.messages.some(m => m.sourceType === 'Communication');
      }
    }
    // Filter by search term
    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      return thread.title.toLowerCase().includes(searchLower) || thread.moduleId.toLowerCase().includes(searchLower) || thread.participants.some(p => p.name.toLowerCase().includes(searchLower)) || thread.messages.some(m => m.content.toLowerCase().includes(searchLower));
    }
    return true;
  });
  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(date);
  };
  // Get relative time (e.g., "2 hours ago")
  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.round(diffMs / 1000);
    const diffMin = Math.round(diffSec / 60);
    const diffHour = Math.round(diffMin / 60);
    const diffDay = Math.round(diffHour / 24);
    if (diffSec < 60) {
      return 'Just now';
    } else if (diffMin < 60) {
      return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
    } else if (diffHour < 24) {
      return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
    } else if (diffDay < 7) {
      return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
    } else {
      return formatDate(dateString);
    }
  };
  // Render priority badge
  const renderPriorityBadge = (priority: 'High' | 'Medium' | 'Low') => {
    let bgColor = 'bg-green-100 text-green-800';
    if (priority === 'High') {
      bgColor = 'bg-red-100 text-red-800';
    } else if (priority === 'Medium') {
      bgColor = 'bg-yellow-100 text-yellow-800';
    }
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor}`} data-id="element-9044">
        {priority}
      </span>;
  };
  // Render status badge
  const renderStatusBadge = (status: 'Active' | 'Resolved' | 'Pending' | 'Closed') => {
    let bgColor = 'bg-gray-100 text-gray-800';
    if (status === 'Active') {
      bgColor = 'bg-blue-100 text-blue-800';
    } else if (status === 'Resolved') {
      bgColor = 'bg-green-100 text-green-800';
    } else if (status === 'Pending') {
      bgColor = 'bg-yellow-100 text-yellow-800';
    }
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor}`} data-id="element-9045">
        {status}
      </span>;
  };
  // Render visibility indicator
  const renderVisibilityIndicator = (visibility: MessageVisibility, groups?: string[]) => {
    let icon = null;
    let label = '';
    let bgColor = 'bg-gray-100 text-gray-800';
    switch (visibility) {
      case 'Public':
        icon = <GlobeIcon className="h-3 w-3 mr-1" data-id="element-9046" />;
        label = 'Public';
        bgColor = 'bg-green-100 text-green-800';
        break;
      case 'Internal':
        icon = <EyeOffIcon className="h-3 w-3 mr-1" data-id="element-9047" />;
        label = 'Internal Only';
        bgColor = 'bg-blue-100 text-blue-800';
        break;
      case 'Group':
        icon = <UsersIcon className="h-3 w-3 mr-1" data-id="element-9048" />;
        label = groups && groups.length > 0 ? `${groups[0]}${groups.length > 1 ? ` +${groups.length - 1}` : ''}` : 'Restricted';
        bgColor = 'bg-purple-100 text-purple-800';
        break;
      case 'Private':
        icon = <EyeOffIcon className="h-3 w-3 mr-1" data-id="element-9049" />;
        label = 'Private';
        bgColor = 'bg-gray-100 text-gray-800';
        break;
    }
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${bgColor}`} title={groups ? `Visible to: ${groups.join(', ')}` : undefined} data-id="element-9050">
        {icon}
        {label}
      </span>;
  };
  // Render source type badge
  const renderSourceBadge = (sourceType?: string, sourceReference?: string) => {
    if (!sourceType || sourceType === 'Communication') return null;
    let icon = null;
    let label = '';
    let bgColor = 'bg-gray-100 text-gray-800';
    switch (sourceType) {
      case 'Invoice':
        icon = <FileTextIcon className="h-3 w-3 mr-1" data-id="element-9051" />;
        label = sourceReference || 'Invoice';
        bgColor = 'bg-purple-100 text-purple-800';
        break;
      case 'CreditNote':
        icon = <MinusSquareIcon className="h-3 w-3 mr-1" data-id="element-9052" />;
        label = sourceReference || 'Credit Note';
        bgColor = 'bg-green-100 text-green-800';
        break;
    }
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${bgColor}`} data-id="element-9053">
        {icon}
        {label}
      </span>;
  };
  // Check if message should be visible to current user
  const isMessageVisible = (message: Message) => {
    // If the user is a vendor, they can only see public messages
    if (isCurrentUserVendor) {
      return message.visibility === 'Public';
    }
    // Internal users can see all messages except those restricted to groups they're not part of
    if (message.visibility === 'Group' && message.visibilityGroups) {
      // Check if current user is in one of the allowed groups
      // For demo purposes, we'll assume the current user is in the Finance Team and Procurement Team
      const userGroups = ['Finance Team', 'Procurement Team'];
      return message.visibilityGroups.some(group => userGroups.includes(group));
    }
    // All other messages are visible to internal users
    return true;
  };
  // Render threads list view
  const renderThreadsList = () => {
    return <div className="space-y-6" data-id="element-9054">
        <div className="flex justify-between items-center" data-id="element-9055">
          <div data-id="element-9056">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-9057">
              Communications
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-9058">
              Manage communications with vendors and internal stakeholders
            </p>
          </div>
          <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9059">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-9060" />
            New Thread
          </button>
        </div>
        {/* Filters */}
        <div className="bg-white shadow rounded-lg p-4" data-id="element-9061">
          <div className="flex flex-col sm:flex-row justify-between space-y-3 sm:space-y-0 sm:space-x-4" data-id="element-9062">
            <div className="flex flex-1 items-center" data-id="element-9063">
              <div className="relative flex-grow" data-id="element-9064">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9065">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-9066" />
                </div>
                <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search communications..." value={filter.search} onChange={e => setFilter({
                ...filter,
                search: e.target.value
              })} data-id="element-9067" />
              </div>
            </div>
            <div className="flex space-x-2" data-id="element-9068">
              <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filter.status} onChange={e => setFilter({
              ...filter,
              status: e.target.value
            })} data-id="element-9069">
                <option value="all" data-id="element-9070">All Status</option>
                <option value="active" data-id="element-9071">Active</option>
                <option value="pending" data-id="element-9072">Pending</option>
                <option value="resolved" data-id="element-9073">Resolved</option>
                <option value="closed" data-id="element-9074">Closed</option>
              </select>
              <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filter.module} onChange={e => setFilter({
              ...filter,
              module: e.target.value
            })} data-id="element-9075">
                <option value="all" data-id="element-9076">All Modules</option>
                <option value="po" data-id="element-9077">Purchase Orders</option>
                <option value="invoice" data-id="element-9078">Invoices</option>
                <option value="requisition" data-id="element-9079">Requisitions</option>
                <option value="vendor" data-id="element-9080">Vendors</option>
                <option value="contract" data-id="element-9081">Contracts</option>
                <option value="payment" data-id="element-9082">Payments</option>
                <option value="creditnote" data-id="element-9083">Credit Notes</option>
              </select>
              <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filter.priority} onChange={e => setFilter({
              ...filter,
              priority: e.target.value
            })} data-id="element-9084">
                <option value="all" data-id="element-9085">All Priority</option>
                <option value="high" data-id="element-9086">High</option>
                <option value="medium" data-id="element-9087">Medium</option>
                <option value="low" data-id="element-9088">Low</option>
              </select>
              <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filter.source} onChange={e => setFilter({
              ...filter,
              source: e.target.value
            })} data-id="element-9089">
                <option value="all" data-id="element-9090">All Sources</option>
                <option value="communications" data-id="element-9091">Communications Only</option>
                <option value="notes" data-id="element-9092">Notes Only</option>
              </select>
            </div>
          </div>
        </div>
        {/* Threads List */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-9093">
          <ul className="divide-y divide-gray-200" data-id="element-9094">
            {filteredThreads.length > 0 ? filteredThreads.map(thread => <li key={thread.id} data-id="element-9095">
                  <button onClick={() => handleSelectThread(thread)} className="block hover:bg-gray-50 w-full text-left" data-id="element-9096">
                    <div className="px-4 py-4 sm:px-6" data-id="element-9097">
                      <div className="flex items-center justify-between" data-id="element-9098">
                        <div className="flex items-center" data-id="element-9099">
                          <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-9100">
                            {thread.title}
                          </p>
                        </div>
                        <div className="ml-2 flex-shrink-0 flex" data-id="element-9101">
                          {renderPriorityBadge(thread.priority)}
                          <span className="ml-2" data-id="element-9102">
                            {renderStatusBadge(thread.status)}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 sm:flex sm:justify-between" data-id="element-9103">
                        <div className="sm:flex" data-id="element-9104">
                          <p className="flex items-center text-sm text-gray-500" data-id="element-9105">
                            {thread.module === 'PO' && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mr-2" data-id="element-9106">
                                PO
                              </span>}
                            {thread.module === 'Invoice' && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 mr-2" data-id="element-9107">
                                Invoice
                              </span>}
                            {thread.module === 'Requisition' && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mr-2" data-id="element-9108">
                                Requisition
                              </span>}
                            {thread.module === 'Contract' && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 mr-2" data-id="element-9109">
                                Contract
                              </span>}
                            {thread.module === 'Vendor' && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 mr-2" data-id="element-9110">
                                Vendor
                              </span>}
                            {thread.module === 'Payment' && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 mr-2" data-id="element-9111">
                                Payment
                              </span>}
                            {thread.module === 'CreditNote' && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mr-2" data-id="element-9112">
                                Credit Note
                              </span>}
                            {thread.moduleId}
                          </p>
                          <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6" data-id="element-9113">
                            <MessageSquareIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" aria-hidden="true" data-id="element-9114" />
                            {thread.messages.length} messages
                          </p>
                          {/* Show source indicator if thread contains notes */}
                          {thread.messages.some(m => m.sourceType === 'Invoice' || m.sourceType === 'CreditNote') && <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6" data-id="element-9115">
                              <FileTextIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" aria-hidden="true" data-id="element-9116" />
                              Includes notes
                            </p>}
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0" data-id="element-9117">
                          <ClockIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" aria-hidden="true" data-id="element-9118" />
                          <p data-id="element-9119">
                            Last activity{' '}
                            <time dateTime={thread.lastActivity} data-id="element-9120">
                              {getRelativeTime(thread.lastActivity)}
                            </time>
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1" data-id="element-9121">
                        {thread.tags?.map((tag, index) => <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-9122">
                            {tag}
                          </span>)}
                      </div>
                    </div>
                  </button>
                </li>) : <li className="px-4 py-6 text-center text-sm text-gray-500" data-id="element-9123">
                No communications match your filters. Try adjusting your search
                criteria.
              </li>}
          </ul>
        </div>
      </div>;
  };
  // Render thread detail view
  const renderThreadDetail = () => {
    if (!selectedThread) return null;
    const visibleMessages = selectedThread.messages.filter(isMessageVisible);
    // Filter related notes that could be relevant to this thread
    const relatedModuleNotes = relatedNotes.filter(note => (note.moduleType === selectedThread.module || note.moduleType === 'Invoice' && selectedThread.module === 'Invoice' || note.moduleType === 'CreditNote' && selectedThread.module === 'CreditNote') && !selectedThread.messages.some(m => m.sourceId === note.id));
    return <div className="space-y-6" data-id="element-9124">
        <div className="flex items-center" data-id="element-9125">
          <button onClick={handleBackToThreads} className="mr-4 p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9126">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-9127" />
          </button>
          <div data-id="element-9128">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-9129">
              {selectedThread.title}
            </h3>
            <div className="mt-1 flex items-center text-sm text-gray-500" data-id="element-9130">
              <span className="mr-2" data-id="element-9131">
                {selectedThread.module} #{selectedThread.moduleId}
              </span>
              <span className="mx-2" data-id="element-9132">•</span>
              <span data-id="element-9133">{renderStatusBadge(selectedThread.status)}</span>
              <span className="mx-2" data-id="element-9134">•</span>
              <span data-id="element-9135">{renderPriorityBadge(selectedThread.priority)}</span>
            </div>
          </div>
        </div>
        {/* Participants */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-9136">
          <div className="px-4 py-5 sm:px-6" data-id="element-9137">
            <h3 className="text-sm font-medium text-gray-900" data-id="element-9138">Participants</h3>
          </div>
          <div className="border-t border-gray-200 px-4 py-3" data-id="element-9139">
            <div className="flex flex-wrap gap-2" data-id="element-9140">
              {selectedThread.participants.map(participant => <div key={participant.id} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800" data-id="element-9141">
                  {participant.isVendor ? <GlobeIcon className="h-4 w-4 mr-1 text-green-500" data-id="element-9142" /> : <UserIcon className="h-4 w-4 mr-1 text-indigo-500" data-id="element-9143" />}
                  {participant.name}
                  <span className="ml-1 text-xs text-gray-500" data-id="element-9144">
                    ({participant.role})
                  </span>
                </div>)}
            </div>
          </div>
        </div>
        {/* Messages */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-9145">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-9146">
            <h3 className="text-sm font-medium text-gray-900" data-id="element-9147">Messages</h3>
            <div className="flex space-x-2" data-id="element-9148">
              <span className="text-xs text-gray-500" data-id="element-9149">
                {visibleMessages.length} of {selectedThread.messages.length}{' '}
                messages
                {visibleMessages.length < selectedThread.messages.length && ' (some messages are restricted)'}
              </span>
              {/* Show import notes button if there are related notes */}
              {relatedModuleNotes.length > 0 && <button onClick={() => setShowNoteImport(!showNoteImport)} className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9150">
                  <FileTextIcon className="h-3 w-3 mr-1" data-id="element-9151" />
                  {showNoteImport ? 'Hide Notes' : 'Import Notes'}
                </button>}
            </div>
          </div>
          {/* Note import panel */}
          {showNoteImport && relatedModuleNotes.length > 0 && <div className="border-t border-gray-200 bg-gray-50 px-4 py-3" data-id="element-9152">
              <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-9153">
                Available Notes
              </h4>
              <div className="space-y-2 max-h-60 overflow-y-auto" data-id="element-9154">
                {relatedModuleNotes.map(note => <div key={note.id} className="flex items-start p-2 border border-gray-200 rounded bg-white" data-id="element-9155">
                    <input type="checkbox" id={`note-${note.id}`} checked={selectedNotes.includes(note.id)} onChange={() => handleToggleNoteSelection(note.id)} className="h-4 w-4 mt-1 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-9156" />
                    <div className="ml-3 flex-1" data-id="element-9157">
                      <div className="flex justify-between" data-id="element-9158">
                        <label htmlFor={`note-${note.id}`} className="text-sm font-medium text-gray-700" data-id="element-9159">
                          {note.moduleType} #{note.moduleId}
                        </label>
                        <span className="text-xs text-gray-500" data-id="element-9160">
                          {new Date(note.createdAt).toLocaleDateString(locale)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1" data-id="element-9161">{note.body}</p>
                      <div className="mt-1 flex items-center" data-id="element-9162">
                        <span className="text-xs text-gray-500 mr-2" data-id="element-9163">
                          By {note.authorName}
                        </span>
                        {renderVisibilityIndicator(note.visibility === 'Public' ? 'Public' : note.visibility === 'Internal' ? 'Internal' : note.visibility === 'Role-based' || note.visibility === 'User-specific' ? 'Group' : 'Private')}
                      </div>
                    </div>
                  </div>)}
              </div>
              <div className="mt-3 flex justify-end" data-id="element-9164">
                <button onClick={() => setShowNoteImport(false)} className="mr-2 inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9165">
                  Cancel
                </button>
                <button onClick={handleImportNotes} disabled={selectedNotes.length === 0} className={`inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded-md text-white ${selectedNotes.length === 0 ? 'bg-indigo-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'}`} data-id="element-9166">
                  Import Selected Notes
                </button>
              </div>
            </div>}
          <div className="border-t border-gray-200" data-id="element-9167">
            <div className="divide-y divide-gray-200" data-id="element-9168">
              {visibleMessages.map(message => <div key={message.id} className="px-4 py-4" data-id="element-9169">
                  <div className="flex justify-between items-start" data-id="element-9170">
                    <div className="flex items-start" data-id="element-9171">
                      <div className="mr-3" data-id="element-9172">
                        <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center" data-id="element-9173">
                          <span className="text-sm font-medium text-indigo-800" data-id="element-9174">
                            {message.sender.charAt(0)}
                          </span>
                        </div>
                      </div>
                      <div data-id="element-9175">
                        <div className="flex items-center" data-id="element-9176">
                          <p className="text-sm font-medium text-gray-900" data-id="element-9177">
                            {message.sender}
                          </p>
                          <span className="ml-2 text-xs text-gray-500" data-id="element-9178">
                            {message.senderRole}
                          </span>
                          {/* Source badge for imported notes */}
                          {message.sourceType && message.sourceType !== 'Communication' && <span className="ml-2" data-id="element-9179">
                                {renderSourceBadge(message.sourceType, message.sourceReference)}
                              </span>}
                        </div>
                        <p className="mt-1 text-sm text-gray-700 whitespace-pre-line" data-id="element-9180">
                          {message.content}
                        </p>
                        {message.attachments && message.attachments.length > 0 && <div className="mt-2" data-id="element-9181">
                              {message.attachments.map(attachment => <div key={attachment.id} className="mt-1 flex items-center text-sm text-gray-500" data-id="element-9182">
                                  <PaperclipIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-9183" />
                                  <span className="text-indigo-600 hover:text-indigo-500 cursor-pointer" data-id="element-9184">
                                    {attachment.name}
                                  </span>
                                  <span className="ml-1" data-id="element-9185">
                                    ({attachment.size})
                                  </span>
                                  <DownloadIcon className="ml-2 h-4 w-4 text-gray-400 hover:text-gray-500 cursor-pointer" data-id="element-9186" />
                                </div>)}
                            </div>}
                        {/* Link to source for imported notes */}
                        {message.sourceType && message.sourceType !== 'Communication' && message.sourceReference && <div className="mt-2" data-id="element-9187">
                              <a href="#" className="inline-flex items-center text-xs text-indigo-600 hover:text-indigo-800" data-id="element-9188">
                                <ExternalLinkIcon className="h-3 w-3 mr-1" data-id="element-9189" />
                                View in{' '}
                                {message.sourceType === 'Invoice' ? 'Invoice' : 'Credit Note'}{' '}
                                {message.sourceReference}
                              </a>
                            </div>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end" data-id="element-9190">
                      <time dateTime={message.timestamp} className="text-xs text-gray-500" data-id="element-9191">
                        {formatDate(message.timestamp)}
                      </time>
                      <div className="mt-1" data-id="element-9192">
                        {renderVisibilityIndicator(message.visibility, message.visibilityGroups)}
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
        {/* Reply Form */}
        <div className="bg-white shadow sm:rounded-lg" data-id="element-9193">
          <div className="px-4 py-5 sm:p-6" data-id="element-9194">
            <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-9195">Reply</h3>
            <div className="mt-1" data-id="element-9196">
              <textarea rows={4} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Type your message here..." value={newMessage} onChange={e => setNewMessage(e.target.value)} data-id="element-9197"></textarea>
            </div>
            {/* Visibility Selector */}
            <div className="mt-4 flex justify-between items-center" data-id="element-9198">
              <div className="relative" data-id="element-9199">
                <button type="button" className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={() => setShowVisibilitySelector(!showVisibilitySelector)} data-id="element-9200">
                  {messageVisibility === 'Public' && <GlobeIcon className="h-4 w-4 mr-2 text-green-500" data-id="element-9201" />}
                  {messageVisibility === 'Internal' && <EyeOffIcon className="h-4 w-4 mr-2 text-blue-500" data-id="element-9202" />}
                  {messageVisibility === 'Group' && <UsersIcon className="h-4 w-4 mr-2 text-purple-500" data-id="element-9203" />}
                  {messageVisibility === 'Private' && <EyeOffIcon className="h-4 w-4 mr-2 text-gray-500" data-id="element-9204" />}
                  {messageVisibility}
                  {messageVisibility === 'Group' && selectedGroups.length > 0 && <span className="ml-1 text-xs" data-id="element-9205">
                        ({selectedGroups.length})
                      </span>}
                  <ChevronDownIcon className="ml-2 h-4 w-4" data-id="element-9206" />
                </button>
                {showVisibilitySelector && <div className="origin-top-left absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" data-id="element-9207">
                    <div className="py-1" role="menu" aria-orientation="vertical" data-id="element-9208">
                      <button className={`${messageVisibility === 'Public' ? 'bg-gray-100' : ''} flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100`} onClick={() => {
                    setMessageVisibility('Public');
                    setShowVisibilitySelector(false);
                  }} data-id="element-9209">
                        <GlobeIcon className="h-4 w-4 mr-2 text-green-500" data-id="element-9210" />
                        Public
                        <span className="ml-2 text-xs text-gray-500" data-id="element-9211">
                          (Visible to vendors)
                        </span>
                      </button>
                      <button className={`${messageVisibility === 'Internal' ? 'bg-gray-100' : ''} flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100`} onClick={() => {
                    setMessageVisibility('Internal');
                    setShowVisibilitySelector(false);
                  }} data-id="element-9212">
                        <EyeOffIcon className="h-4 w-4 mr-2 text-blue-500" data-id="element-9213" />
                        Internal Only
                        <span className="ml-2 text-xs text-gray-500" data-id="element-9214">
                          (Not visible to vendors)
                        </span>
                      </button>
                      <button className={`${messageVisibility === 'Group' ? 'bg-gray-100' : ''} flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100`} onClick={() => {
                    setMessageVisibility('Group');
                  }} data-id="element-9215">
                        <UsersIcon className="h-4 w-4 mr-2 text-purple-500" data-id="element-9216" />
                        Restricted to Groups
                      </button>
                      {messageVisibility === 'Group' && <div className="px-4 py-2 border-t border-gray-100" data-id="element-9217">
                          <p className="text-xs font-medium text-gray-500 mb-2" data-id="element-9218">
                            Select groups:
                          </p>
                          <div className="space-y-2 max-h-40 overflow-y-auto" data-id="element-9219">
                            {userGroups.map(group => <div key={group.id} className="flex items-center" data-id="element-9220">
                                <input id={`group-${group.id}`} type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked={selectedGroups.includes(group.name)} onChange={() => handleToggleGroup(group.name)} data-id="element-9221" />
                                <label htmlFor={`group-${group.id}`} className="ml-2 block text-sm text-gray-700" data-id="element-9222">
                                  {group.name}
                                </label>
                              </div>)}
                          </div>
                          <div className="mt-2 flex justify-end" data-id="element-9223">
                            <button type="button" className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={() => setShowVisibilitySelector(false)} data-id="element-9224">
                              <CheckIcon className="h-3 w-3 mr-1" data-id="element-9225" />
                              Done
                            </button>
                          </div>
                        </div>}
                    </div>
                  </div>}
              </div>
              <div className="flex space-x-3" data-id="element-9226">
                <button type="button" className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9227">
                  <PaperclipIcon className="h-4 w-4 mr-2" data-id="element-9228" />
                  Attach
                </button>
                <button type="button" onClick={handleSendMessage} disabled={!newMessage.trim()} className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${!newMessage.trim() ? 'bg-indigo-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'}`} data-id="element-9229">
                  <SendIcon className="h-4 w-4 mr-2" data-id="element-9230" />
                  Send
                </button>
              </div>
            </div>
            {/* Visibility Warning for Public Messages */}
            {messageVisibility === 'Public' && <div className="mt-3 flex items-center text-sm text-amber-600" data-id="element-9231">
                <EyeIcon className="h-4 w-4 mr-1" data-id="element-9232" />
                This message will be visible to all participants, including
                vendors.
              </div>}
          </div>
        </div>
      </div>;
  };
  return <div className="space-y-6" data-id="element-9233">
      <div className="mb-8" data-id="element-9234">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-9235">Communications</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-9236">
          Manage communications with vendors and internal stakeholders
        </p>
      </div>
      {activeView === 'threads' && renderThreadsList()}
      {activeView === 'thread-detail' && renderThreadDetail()}
    </div>;
}